import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartAddPendingEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { formatMoney, applyPercentageDiscountEach, parseMoneyProbeToMinorUnits } from '@theme/money-formatting';
import {
  startCartLinesAdd,
  resolveCartLinesUpdate,
  rejectCartLinesUpdate,
} from '@theme/standard-events-dispatch';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

const PRICE_TOKEN = '[price]';
const COMPARE_PRICE_TOKEN = '[compare-price]';

/**
 * Replace merchant [price] / [compare-price] tokens. Empty compare omits the
 * strike and collapses leftover spaces. No invented label if a token is absent.
 * @param {Element} labelElement
 * @param {string} template
 * @param {string} saleText
 * @param {string} [compareText]
 */
function applyButtonPriceTokens(labelElement, template, saleText, compareText = '') {
  const hasCompareToken = template.includes(COMPARE_PRICE_TOKEN);
  const compareHtml = hasCompareToken && compareText
    ? `<s class="compare-at-price">${compareText}</s>`
    : '';
  const html = template
    .replaceAll(COMPARE_PRICE_TOKEN, compareHtml)
    .replaceAll(PRICE_TOKEN, saleText)
    .replace(/ {2,}/g, ' ')
    .trim();

  if (compareHtml) {
    labelElement.innerHTML = html;
    return;
  }

  labelElement.textContent = html;
}

/**
 * Quantity input associated with a product form, including controls outside the form
 * that use the HTML form="" attribute (e.g. bon [data-bon-quantity-input]).
 * form.querySelector('input[name="quantity"]') misses those; form.elements.namedItem does not.
 * @param {HTMLFormElement} form
 * @returns {HTMLInputElement | null}
 */
function getAssociatedQuantityInput(form) {
  const named = form.elements.namedItem('quantity');
  if (named instanceof RadioNodeList) {
    const first = named[0];
    return first instanceof HTMLInputElement ? first : null;
  }
  return named instanceof HTMLInputElement ? named : null;
}

/**
 * @param {NodeListOf<AddToCartComponent>} addToCartContainers
 */
function redirectToCheckout(addToCartContainers) {
  for (const container of addToCartContainers) {
    if (container.refs.addToCartButton?.dataset.buttonAction === 'straight-to-checkout') {
      delete container.refs.addToCartButton.dataset.buttonAction;
    }
  }

  window.location.href = '/checkout';
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  /** @type {Element | null} */
  #variantEventTarget = null;

  /** @type {HTMLElement | null} */
  #formElement = null;

  /** @type {(() => void) | null} */
  #handlePageShow = null;

  /** @type {MutationObserver | null} */
  #igPriceObserver = null;

  /** @type {boolean} */
  #brickIncludeAddonsInCta = false;

  /** @type {string} */
  #brickCartTotalText = '';

  /** @type {string} */
  #brickCartCompareText = '';

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);

    this.#formElement = this.closest('product-form-component');
    this.#formElement?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdateEvent);

    this.#variantEventTarget = this.closest('.shopify-section, dialog, product-card');
    this.#variantEventTarget?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdateEvent);

    this.#handlePageShow = () => {
      this.refs.addToCartButton?.removeAttribute('data-added');
    };
    window.addEventListener('pageshow', this.#handlePageShow);
    document.addEventListener('brick:price-updated', this.#handleBrickPriceUpdated);

    this.syncDynamicPriceLabel();

    // IG price test: re-render the dynamic price label when the tested-price probe changes.
    const igProbe = this.querySelector('[data-atc-ig-unit-price]');
    if (igProbe && 'MutationObserver' in window) {
      this.#igPriceObserver = new MutationObserver(() => this.syncDynamicPriceLabel());
      this.#igPriceObserver.observe(igProbe, { childList: true, characterData: true, subtree: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);

    this.#formElement?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdateEvent);
    this.#variantEventTarget?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdateEvent);
    document.removeEventListener('brick:price-updated', this.#handleBrickPriceUpdated);

    if (this.#handlePageShow) {
      window.removeEventListener('pageshow', this.#handlePageShow);
      this.#handlePageShow = null;
    }

    if (this.#igPriceObserver) {
      this.#igPriceObserver.disconnect();
      this.#igPriceObserver = null;
    }

    this.#formElement = null;
    this.#variantEventTarget = null;
  }

  /**
   * Only adopt brick:price-updated totals published from this ATC's own scope
   * (section / dialog / product card). Secondary ATCs elsewhere on the page keep
   * their own variant × qty.
   * @param {unknown} source
   */
  #isOwnBrickPriceSource(source) {
    return source instanceof Node && this.#variantEventTarget?.contains(source) === true;
  }

  /**
   * Quantity selector publishes CTA totals on brick:price-updated with
   * includeAddonsInCta true (same numbers as the sticky bar). Prefer those over
   * variant × qty so [price] / [compare-price] stay in sync.
   * @param {Event} event
   */
  #handleBrickPriceUpdated = (event) => {
    const detail = /** @type {{ source?: unknown, includeAddonsInCta?: boolean, totalPriceText?: string, originalPriceText?: string }} */ (
      'detail' in event ? event.detail : {}
    ) || {};
    if (!this.#isOwnBrickPriceSource(detail.source)) return;

    this.#brickIncludeAddonsInCta = detail.includeAddonsInCta === true;
    this.#brickCartTotalText = typeof detail.totalPriceText === 'string' ? detail.totalPriceText : '';
    this.#brickCartCompareText = typeof detail.originalPriceText === 'string' ? detail.originalPriceText : '';

    if (this.#brickIncludeAddonsInCta && this.#brickCartTotalText) {
      this.#applyPriceTemplate(this.#brickCartTotalText, this.#brickCartCompareText);
    }
  };

  /**
   * @param {string} formattedPrice
   * @param {string} [formattedCompare]
   */
  #applyPriceTemplate(formattedPrice, formattedCompare = '') {
    const button = this.refs.addToCartButton;
    if (!button || button.disabled) return;

    const priceTemplate = button.dataset.priceTemplate;
    const labelElement = button.querySelector('.add-to-cart-text__label');
    if (!priceTemplate || !labelElement) return;

    applyButtonPriceTokens(labelElement, priceTemplate, formattedPrice, formattedCompare);
  }

  /** @param {number} [quantityOverride] */
  syncDynamicPriceLabel(quantityOverride) {
    const button = this.refs.addToCartButton;
    if (!button || button.disabled) return;

    if (this.#brickIncludeAddonsInCta && this.#brickCartTotalText) {
      this.#applyPriceTemplate(this.#brickCartTotalText, this.#brickCartCompareText);
      return;
    }

    const priceTemplate = button.dataset.priceTemplate;
    if (!priceTemplate) return;

    const moneyFormat = button.dataset.moneyFormat;
    const currency = button.dataset.currency;
    if (!moneyFormat || !currency) return;

    // IG price test: prefer the tested per-unit price from the hidden probe (IG rewrites it),
    // falling back to the theme's static price when no test is running.
    const igProbe = this.querySelector('[data-atc-ig-unit-price]');
    const testedUnit = igProbe ? parseMoneyProbeToMinorUnits(igProbe.textContent, currency) : NaN;
    const unitPrice = (Number.isFinite(testedUnit) && testedUnit > 0)
      ? testedUnit
      : Number(button.dataset.variantPrice || '0');
    if (!Number.isFinite(unitPrice) || unitPrice <= 0) return;

    const quantity = typeof quantityOverride === 'number' ? quantityOverride : this.#getCurrentFormQuantity();
    const lineQuantity = Math.max(1, quantity);

    const totalPrice = unitPrice * lineQuantity;
    const displayDiscount = Number(button.dataset.displayDiscount || '0');
    const labelElement = button.querySelector('.add-to-cart-text__label');
    if (!labelElement) return;

    if (Number.isFinite(displayDiscount) && displayDiscount > 0) {
      const originalPrice = totalPrice;
      const salePrice = applyPercentageDiscountEach(unitPrice, lineQuantity, displayDiscount);
      const formattedSale = formatMoney(salePrice, moneyFormat, currency, { stripTrailingZeros: true });
      const formattedOriginal = formatMoney(originalPrice, moneyFormat, currency, { stripTrailingZeros: true });

      if (priceTemplate.includes(COMPARE_PRICE_TOKEN)) {
        applyButtonPriceTokens(labelElement, priceTemplate, formattedSale, formattedOriginal);
        return;
      }

      const priceHtml = `${formattedSale} <s class="compare-at-price">${formattedOriginal}</s>`;
      labelElement.innerHTML = priceTemplate.replaceAll(PRICE_TOKEN, priceHtml);
      return;
    }

    const compareAtUnit = Number(button.dataset.variantCompareAtPrice || '0');
    const formattedPrice = formatMoney(totalPrice, moneyFormat, currency, { stripTrailingZeros: true });
    if (
      Number.isFinite(compareAtUnit) &&
      compareAtUnit > unitPrice &&
      priceTemplate.includes(COMPARE_PRICE_TOKEN)
    ) {
      const formattedCompare = formatMoney(compareAtUnit * lineQuantity, moneyFormat, currency, {
        stripTrailingZeros: true,
      });
      applyButtonPriceTokens(labelElement, priceTemplate, formattedPrice, formattedCompare);
      return;
    }

    applyButtonPriceTokens(labelElement, priceTemplate, formattedPrice);
  }

  #getCurrentFormQuantity() {
    const form = this.#formElement;
    if (!(form instanceof HTMLFormElement)) return 1;

    const associated = getAssociatedQuantityInput(form);
    return Number.parseInt(associated?.value || '1', 10) || 1;
  }

  /** @param {Event} event */
  #handleQuantityUpdateEvent = (event) => {
    const quantityEvent = /** @type {import('./events').QuantitySelectorUpdateEvent} */ (event);
    this.#handleQuantityUpdate(quantityEvent);
  };

  /** @param {import('./events').QuantitySelectorUpdateEvent} event */
  #handleQuantityUpdate(event) {
    if (event.detail.cartLine) return;
    this.syncDynamicPriceLabel(event.detail.quantity);
  }

  /** @param {Event} event */
  #handleVariantUpdateEvent = (event) => {
    const variantEvent = /** @type {VariantUpdateEvent} */ (event);
    this.#handleVariantUpdate(variantEvent);
  };

  /** @param {VariantUpdateEvent} _event */
  #handleVariantUpdate(_event) {
    // Variant morph updates data attributes; resync dynamic [price] with current quantity.
    this.syncDynamicPriceLabel();
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async () => {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {((event: PageTransitionEvent) => void) | null} */
  #handlePageShow = null;

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number, buttonAction?: string}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdateEvent, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelectedEvent, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdateEvent, { signal });

    this.#handlePageShow = () => {
      this.#resetNativeCheckoutFormState();
    };
    window.addEventListener('pageshow', this.#handlePageShow);

    this.#associateSectionFormFields();
    this.#resetNativeCheckoutFormState();
  }

  /**
   * Point external form="" controls (quantity, custom properties) at this form.
   * Form ids are BuyButtons-ProductForm-{{ section.id }}-{{ block.id }} for uniqueness
   * when multiple buy-buttons share a section. Scope to the section so two ATCs
   * in one section do not steal each other's fields (last connect wins).
   */
  #associateSectionFormFields() {
    const form = this.querySelector('form');
    const sectionId = this.dataset.sectionId;
    if (!(form instanceof HTMLFormElement) || !sectionId || !form.id) return;

    const sectionRoot =
      this.closest(`#shopify-section-${CSS.escape(sectionId)}`) ??
      this.closest('.shopify-section');
    if (!(sectionRoot instanceof HTMLElement)) return;

    const legacyFormId = `BuyButtons-ProductForm-${sectionId}`;
    const selector = [
      `[data-product-form-section="${CSS.escape(sectionId)}"]`,
      `[form="${CSS.escape(legacyFormId)}"]`,
    ].join(', ');

    for (const el of sectionRoot.querySelectorAll(selector)) {
      if (!(el instanceof HTMLElement)) continue;
      // Only rebind legacy / unbound controls — never steal another block's form id.
      const currentForm = el.getAttribute('form');
      if (currentForm && currentForm !== legacyFormId && currentForm !== form.id) continue;
      el.setAttribute('form', form.id);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    if (this.#handlePageShow) {
      window.removeEventListener('pageshow', this.#handlePageShow);
      this.#handlePageShow = null;
    }
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  #handleCartUpdateEvent = (event) => {
    void this.#onCartUpdate(/** @type {CartUpdateEvent|CartAddEvent} */ (event));
  };

  /** @param {Event} event */
  handleSubmit(event) {
    const submitter =
      'submitter' in event && event.submitter instanceof HTMLButtonElement ? event.submitter : undefined;
    const submitterButtonAction = submitter?.dataset.buttonAction;

    const addToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (this.querySelectorAll('add-to-cart-component'));
    const buttonAction =
      submitterButtonAction ||
      Array.from(addToCartContainers)
        .map((container) => container.refs.addToCartButton?.dataset.buttonAction)
        .find(Boolean);

    const isStraightToCheckout = buttonAction === 'straight-to-checkout';

    if (isStraightToCheckout && !this.#variantChangeInProgress) {
      this.#submitNativelyToCheckout(submitter, addToCartContainers);
      return;
    }

    this.#resetNativeCheckoutFormState();
    this.#setFormReturnTo('');

    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity, buttonAction });
        if (!isStraightToCheckout) {
          this.#emitAddPending(intendedVariantId);
        }
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event, submitterButtonAction);
  }

  /**
   * @param {HTMLButtonElement | undefined} submitter
   * @param {NodeListOf<AddToCartComponent>} addToCartContainers
   */
  #submitNativelyToCheckout(submitter, addToCartContainers) {
    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    if (!this.refs.variantId?.value) return;

    const upsellSelections = this.#getUpsellSelections();
    this.#prepareNativeCheckoutFormState(form, upsellSelections);
    this.#setFormReturnTo('/checkout');

    // Sticky proxy clicks can leave temporary checkout intent on the main button.
    for (const container of addToCartContainers) {
      const button = container.refs.addToCartButton;
      if (!button) continue;

      if (button.dataset.puppet === 'true') {
        button.dataset.puppet = 'false';
        delete button.dataset.buttonAction;
      }
    }

    if (submitter && submitter.form === form && typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter);
      return;
    }

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
      return;
    }

    form.submit();
  }

  /** @param {string} value */
  #setFormReturnTo(value) {
    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    const returnToInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="return_to"]'));
    if (returnToInput) {
      returnToInput.value = value;
      return;
    }

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'return_to';
    input.value = value;
    form.appendChild(input);
  }

  /**
   * @param {HTMLFormElement} form
   * @param {{variantId: string, quantity: number, label: string, title: string}[]} upsellSelections
   */
  #prepareNativeCheckoutFormState(form, upsellSelections) {
    this.#resetNativeCheckoutFormState();

    if (!upsellSelections.length) return;

    const primaryVariantId = this.refs.variantId?.value;
    if (!primaryVariantId) return;

    const primaryQuantity = Math.max(1, this.#getQuantity());

    this.#rememberAndRenameField(this.refs.variantId, 'items[0][id]');

    // Prefer form.elements (includes form=""-associated bon quantity) over form.querySelector
    // which only finds descendants and missed the synced hidden quantity outside the form.
    const quantityInput = getAssociatedQuantityInput(form);
    if (quantityInput) {
      quantityInput.value = primaryQuantity.toString();
      this.#rememberAndRenameField(quantityInput, 'items[0][quantity]');
    } else {
      this.#appendNativeCheckoutTempInput(form, 'items[0][quantity]', primaryQuantity.toString());
    }

    const propertyInputs = form.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]');
    for (const field of propertyInputs) {
      const currentName = field.getAttribute('name') || '';
      const match = currentName.match(/^properties\[(.+)\]$/);
      if (!match || !match[1]) continue;
      this.#rememberAndRenameField(field, `items[0][properties][${match[1]}]`);
    }

    const sellingPlanFields = form.querySelectorAll('[name="selling_plan"]');
    for (const field of sellingPlanFields) {
      this.#rememberAndRenameField(field, 'items[0][selling_plan]');
    }

    for (const [index, upsellSelection] of upsellSelections.entries()) {
      const itemIndex = index + 1;
      this.#appendNativeCheckoutTempInput(form, `items[${itemIndex}][id]`, upsellSelection.variantId);
      this.#appendNativeCheckoutTempInput(form, `items[${itemIndex}][quantity]`, upsellSelection.quantity.toString());
      this.#appendNativeCheckoutTempInput(form, `items[${itemIndex}][properties][_upsell_label]`, upsellSelection.label || 'Upsell');
      this.#appendNativeCheckoutTempInput(form, `items[${itemIndex}][properties][_upsell_title]`, upsellSelection.title || 'Upsell item');
    }
  }

  #resetNativeCheckoutFormState() {
    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    const tempInputs = form.querySelectorAll('input[data-native-checkout-temp="true"]');
    for (const input of tempInputs) {
      input.remove();
    }

    // Restore renamed fields inside the form and form-associated controls outside it
    // (e.g. bon [data-bon-quantity-input] with form="...").
    const formId = form.getAttribute('id');
    const renamedFields = [
      ...form.querySelectorAll('[data-native-checkout-original-name]'),
      ...(formId
        ? document.querySelectorAll(`[form="${CSS.escape(formId)}"][data-native-checkout-original-name]`)
        : []),
    ];
    for (const field of renamedFields) {
      const originalName = field.getAttribute('data-native-checkout-original-name');
      if (originalName != null) {
        field.setAttribute('name', originalName);
      }
      field.removeAttribute('data-native-checkout-original-name');
    }
  }

  /**
   * @param {Element} field
   * @param {string} newName
   */
  #rememberAndRenameField(field, newName) {
    const currentName = field.getAttribute('name');
    if (!currentName) return;

    if (!field.hasAttribute('data-native-checkout-original-name')) {
      field.setAttribute('data-native-checkout-original-name', currentName);
    }

    field.setAttribute('name', newName);
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} name
   * @param {string} value
   */
  #appendNativeCheckoutTempInput(form, name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.setAttribute('data-native-checkout-temp', 'true');
    form.appendChild(input);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    const fromRef = Number(this.refs.quantitySelector?.getValue?.());
    if (Number.isFinite(fromRef) && fromRef > 0) return fromRef;

    const form = /** @type {HTMLFormElement | null} */ (this.querySelector('form'));
    const associated = form ? getAssociatedQuantityInput(form) : null;
    const fromAssociated = Number.parseInt(associated?.value || '', 10);
    if (Number.isFinite(fromAssociated) && fromAssociated > 0) return fromAssociated;

    const section = this.closest('.shopify-section, dialog, product-card');
    const stepper = /** @type {{ getValue?: () => string } | null} */ (
      section?.querySelector('quantity-selector-component') ?? null
    );
    const fromStepper = Number(stepper?.getValue?.());
    if (Number.isFinite(fromStepper) && fromStepper > 0) return fromStepper;

    return Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   * @param {string} [overrideButtonAction]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event, overrideButtonAction) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );
    const shouldGoToCheckout =
      overrideButtonAction === 'straight-to-checkout' ||
      (overrideButtonAction == null &&
        Array.from(allAddToCartContainers).some(
          (container) => container.refs.addToCartButton?.dataset.buttonAction === 'straight-to-checkout'
        ));

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = /** @type {string[]} */ ([]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    const sectionsParam = cartItemComponentsSectionIds.join(',');
    if (sectionsParam) {
      formData.set('sections', sectionsParam);
    }

    const upsellSelections = this.#getUpsellSelections();

    const variantId = formData.get('id') || overrideVariantId;
    const itemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);

    if (!shouldGoToCheckout) {
      this.#emitAddPending(variantId);
    }

    const cartLinesDeferred = startCartLinesAdd({
      element: this,
      lines: [{ merchandiseId: String(variantId || ''), quantity: itemCount }],
      detail: { source: 'product-form-component' },
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          void resolveCartLinesUpdate(cartLinesDeferred, {
            userErrors: [{ message: response.message || 'Add to cart failed', code: 'INVALID' }],
            detail: {
              didError: true,
              source: 'product-form-component',
              sourceId: this.id.toString(),
              itemCount,
              productId: this.dataset.productId,
            },
          });

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount,
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          void resolveCartLinesUpdate(cartLinesDeferred, {
            detail: {
              source: 'product-form-component',
              sourceId: this.id.toString(),
              itemCount,
              productId: this.dataset.productId,
              sections: response.sections,
              didError: false,
            },
          });

          if (!shouldGoToCheckout) {
            void this.#fetchAndUpdateCartQuantity();
          }

          const redirectAfterUpsell = shouldGoToCheckout;
          const upsellPromise = upsellSelections.length
            ? this.#addUpsellItems(upsellSelections, sectionsParam)
            : Promise.resolve({ didError: false });

          upsellPromise.finally(() => {
            if (redirectAfterUpsell) {
              redirectToCheckout(allAddToCartContainers);
            }
          });
        }
      })
      .catch((error) => {
        console.error(error);
        rejectCartLinesUpdate(cartLinesDeferred, error, this);
        const message = error instanceof Error ? error.message : 'Add to cart failed';
        this.dispatchEvent(new CartErrorEvent(form.getAttribute('id') || '', message));
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            cartUnchanged: true,
            source: 'product-form-component',
            itemCount,
            productId: this.dataset.productId,
          })
        );
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * Optimistic cart UI: skeleton + open the native drawer without waiting for cart/add.
   * @param {string | number | FormDataEntryValue | null | undefined} variantId
   */
  #emitAddPending(variantId) {
    this.dispatchEvent(
      new CartAddPendingEvent(this.id, {
        variantId: variantId?.toString() || '',
        productId: this.dataset.productId,
        source: 'product-form-component',
      })
    );
    this.#openNativeCartDrawer();
  }

  /**
   * Open even if cart-drawer.js has not upgraded yet (module is fetchpriority=low).
   * Always defer a frame so the triggering click cannot dismiss the dialog.
   */
  #openNativeCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer?.hasAttribute('auto-open')) return;

    drawer.removeAttribute('hidden');

    if ('openForAdd' in drawer && typeof drawer.openForAdd === 'function') {
      drawer.openForAdd();
      return;
    }

    const dialog = drawer.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement && !dialog.open) {
      requestAnimationFrame(() => {
        try {
          dialog.showModal();
        } catch {
          // InvalidStateError if already closing as a modal
        }
      });
    }
  }

  /** @param {Array<{variantId: string, quantity: number, buttonAction?: string}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const shouldGoToCheckout = items.some((item) => item.buttonAction === 'straight-to-checkout');
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const cartLinesDeferred = startCartLinesAdd({
      element: this,
      lines: items.map((item) => ({ merchandiseId: item.variantId, quantity: item.quantity })),
      detail: { source: 'product-form-component' },
    });

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          void resolveCartLinesUpdate(cartLinesDeferred, {
            userErrors: [{ message: response.message || 'Add to cart failed', code: 'INVALID' }],
            detail: {
              didError: true,
              source: 'product-form-component',
              sourceId: this.id.toString(),
              itemCount: totalQuantity,
              productId: this.dataset.productId,
            },
          });

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: totalQuantity,
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );

        void resolveCartLinesUpdate(cartLinesDeferred, {
          detail: {
            source: 'product-form-component',
            sourceId: this.id.toString(),
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
            didError: false,
          },
        });

        if (!shouldGoToCheckout) {
          void this.#fetchAndUpdateCartQuantity();
        }

        if (shouldGoToCheckout) {
          const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
            this.querySelectorAll('add-to-cart-component')
          );
          redirectToCheckout(allAddToCartContainers);
        }
      })
      .catch((error) => {
        console.error(error);
        rejectCartLinesUpdate(cartLinesDeferred, error, this);
        const message = error instanceof Error ? error.message : 'Add to cart failed';
        this.dispatchEvent(new CartErrorEvent(this.id, message));
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            cartUnchanged: true,
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
          })
        );
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Gather checked upsells within the same section/dialog as the form.
   * @returns {{variantId: string, quantity: number, label: string, title: string}[]}
   */
  #getUpsellSelections() {
    const container = this.closest('.shopify-section, dialog, product-card');
    if (!container) return [];

    const selections = [];
    const upsellContainers = container.querySelectorAll('[data-upsell-container]');

    for (const upsellContainer of upsellContainers) {
      if (!(upsellContainer instanceof HTMLElement)) continue;
      // V4 carousel picker: ignore draft cards until Add (data-carousel-modal-open).
      if (
        upsellContainer.closest('[data-carousel-upsell]') &&
        upsellContainer.closest('[data-carousel-modal-open]')
      ) {
        continue;
      }
      const upsellCheckbox = /** @type {HTMLInputElement | null} */ (
        upsellContainer.querySelector('[data-upsell-checkbox]')
      );
      if (!upsellCheckbox || !upsellCheckbox.checked || upsellCheckbox.disabled) continue;

      const variantId = upsellContainer.dataset.upsellVariantId;
      if (!variantId) continue;

      const quantityValue = upsellCheckbox.dataset.upsellQuantity || upsellCheckbox.value || '1';
      const quantity = Number.parseInt(quantityValue, 10) || 1;
      selections.push({
        variantId,
        quantity,
        label: upsellContainer.dataset.upsellLabel || 'Upsell',
        title: upsellContainer.dataset.upsellTitle || 'Upsell item',
      });
    }

    return selections;
  }

  /**
   * Add checked upsells as separate line items in one cart/add request.
   * @param {{variantId: string, quantity: number, label: string, title: string}[]} upsellSelections
   * @param {string} sectionsParam
   * @returns {Promise<{didError: boolean}>}
   */
  async #addUpsellItems(upsellSelections, sectionsParam) {
    if (!upsellSelections.length) return { didError: false };

    const form = this.querySelector('form');
    const formData = new FormData();

    for (const [index, upsellSelection] of upsellSelections.entries()) {
      formData.append(`items[${index}][id]`, upsellSelection.variantId);
      formData.append(`items[${index}][quantity]`, upsellSelection.quantity.toString());
      formData.append(`items[${index}][properties][_upsell_label]`, upsellSelection.label || 'Upsell');
      formData.append(`items[${index}][properties][_upsell_title]`, upsellSelection.title);
    }

    if (sectionsParam) {
      formData.append('sections', sectionsParam);
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });
    let itemCount = 0;
    for (const selection of upsellSelections) {
      itemCount += selection.quantity;
    }
    const cartLinesDeferred = startCartLinesAdd({
      element: this,
      lines: upsellSelections.map((selection) => ({
        merchandiseId: selection.variantId,
        quantity: selection.quantity,
      })),
      detail: { source: 'product-form-upsell' },
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });
      const payload = await response.json();

      if (payload.status) {
        window.dispatchEvent(
          new CartErrorEvent(
            form?.getAttribute('id') || this.id || '',
            payload.message,
            payload.description,
            payload.errors
          )
        );
        await resolveCartLinesUpdate(cartLinesDeferred, {
          userErrors: [{ message: payload.message || 'Add to cart failed', code: 'INVALID' }],
          detail: { didError: true, source: 'product-form-upsell' },
        });
        return { didError: true };
      }

      const addedItem = Array.isArray(payload.items) ? payload.items[0] : null;

      this.dispatchEvent(
        new CartAddEvent({}, upsellSelections[0].variantId.toString(), {
          source: 'product-form-component',
          itemCount,
          productId: addedItem?.product_id?.toString?.(),
          sections: payload.sections,
        })
      );

      await resolveCartLinesUpdate(cartLinesDeferred, {
        detail: {
          source: 'product-form-upsell',
          itemCount,
          productId: addedItem?.product_id?.toString?.(),
          sections: payload.sections,
          didError: false,
        },
      });

      return { didError: false };
    } catch (error) {
      console.error(error);
      rejectCartLinesUpdate(cartLinesDeferred, error, this);
      return { didError: true };
    }
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const details = document.querySelector('[data-testid="product-information-details"]');
    if (details instanceof HTMLElement) {
      details.dataset.variantAvailable =
        event.detail.resource == null || event.detail.resource.available == false ? 'false' : 'true';
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
      currentAddToCartButtonContainer.syncDynamicPriceLabel?.();
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {Event} event */
  #handleVariantUpdateEvent = (event) => {
    void this.#onVariantUpdate(/** @type {VariantUpdateEvent} */ (event));
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };

  /** @param {Event} event */
  #handleVariantSelectedEvent = (event) => {
    this.#onVariantSelected(/** @type {import('./events').VariantSelectedEvent} */ (event));
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
